// @ts-nocheck
import React, { useState } from 'react';
import {
  Typography,
  Card,
  Box,
  TextField,
  Button,
  Stack,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Snackbar,
  IconButton,
  Popover,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Iconify from '../../components/iconify';
import useGetAvailableModels, { LLMModel } from '../../hooks/KnowledgeAI/useGetAvailableModels';
import useAddModel from '../../hooks/KnowledgeAI/useAddModel';
import useDeleteModel from '../../hooks/KnowledgeAI/useDeleteModel';
import useUpdateModel from '../../hooks/KnowledgeAI/useUpdateModel';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'id', label: 'ID' },
  { id: 'model_name', label: 'Model Name' },
  { id: 'deployment', label: 'Deployment' },
  { id: 'cost_prompt', label: 'Cost (Prompt)' },
  { id: 'cost_completion', label: 'Cost (Completion)' },
  { id: 'actions', label: '' },
];

// ----------------------------------------------------------------------

type AddModelErrors = {
  deploymentName?: string;
  modelName?: string;
  costPrompt?: string;
  costCompletion?: string;
};

export default function ModelConfiguration() {
  const { data: models, loading: modelsLoading, error: modelsError, refetch } = useGetAvailableModels();
  const { addModel, loading: addLoading } = useAddModel();
  const { deleteModel, loading: deleteLoading } = useDeleteModel();
  const { updateModel, loading: updateLoading } = useUpdateModel();

  const [showAddModel, setShowAddModel] = useState(false);
  const [deploymentName, setDeploymentName] = useState('');
  const [modelName, setModelName] = useState('');
  const [costPrompt, setCostPrompt] = useState('');
  const [costCompletion, setCostCompletion] = useState('');
  const [addModelErrors, setAddModelErrors] = useState<AddModelErrors>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Three-dots menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedModel, setSelectedModel] = useState<LLMModel | null>(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<{
    deployment: string;
    sdk_type: string;
    max_tokens: string;
    cost_prompt: string;
    cost_completion: string;
    description: string;
  }>({ deployment: '', sdk_type: '', max_tokens: '', cost_prompt: '', cost_completion: '', description: '' });

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, model: LLMModel) => {
    setMenuAnchor(event.currentTarget);
    setSelectedModel(model);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedModel(null);
  };

  const handleOpenEdit = () => {
    if (!selectedModel) return;
    setEditForm({
      deployment: selectedModel.deployment ?? '',
      sdk_type: (selectedModel as any).sdk_type ?? '',
      max_tokens: String((selectedModel as any).max_tokens ?? ''),
      cost_prompt: String(selectedModel.cost_prompt ?? ''),
      cost_completion: String(selectedModel.cost_completion ?? ''),
      description: selectedModel.description ?? '',
    });
    setMenuAnchor(null);
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setSelectedModel(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedModel) return;
    const payload: any = { model_name: selectedModel.model_name };
    if (editForm.deployment.trim()) payload.deployment = editForm.deployment.trim();
    if (editForm.sdk_type.trim()) payload.sdk_type = editForm.sdk_type.trim();
    if (editForm.max_tokens.trim()) payload.max_tokens = Number(editForm.max_tokens);
    if (editForm.cost_prompt.trim()) payload.cost_prompt = Number(editForm.cost_prompt);
    if (editForm.cost_completion.trim()) payload.cost_completion = Number(editForm.cost_completion);
    payload.description = editForm.description;

    const success = await updateModel(payload);
    if (success) {
      setSnackbar({ open: true, message: 'Model updated successfully.', severity: 'success' });
      handleCloseEdit();
      refetch();
    } else {
      setSnackbar({ open: true, message: 'Failed to update model. Please try again.', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!selectedModel) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete model "${selectedModel.model_name}"? This action cannot be undone.`
    );
    if (!confirmed) {
      handleCloseMenu();
      return;
    }
    handleCloseMenu();
    const success = await deleteModel(selectedModel.model_name);
    if (success) {
      setSnackbar({ open: true, message: `Model "${selectedModel.model_name}" deleted successfully.`, severity: 'success' });
      refetch();
    } else {
      setSnackbar({ open: true, message: 'Failed to delete model. Please try again.', severity: 'error' });
    }
  };

  const validateAddModel = (): AddModelErrors => {
    const errs: AddModelErrors = {};
    if (!deploymentName.trim()) errs.deploymentName = 'Deployment name is required.';
    if (!modelName.trim()) errs.modelName = 'Model name is required.';
    if (!costPrompt.trim()) {
      errs.costPrompt = 'Cost (Prompt) is required.';
    } else if (Number.isNaN(Number(costPrompt)) || Number(costPrompt) < 0) {
      errs.costPrompt = 'Please enter a valid positive number.';
    }
    if (!costCompletion.trim()) {
      errs.costCompletion = 'Cost (Completion) is required.';
    } else if (Number.isNaN(Number(costCompletion)) || Number(costCompletion) < 0) {
      errs.costCompletion = 'Please enter a valid positive number.';
    }
    return errs;
  };

  const handleAddModel = async () => {
    const errs = validateAddModel();
    if (Object.keys(errs).length > 0) {
      setAddModelErrors(errs);
      return;
    }
    const success = await addModel({
      model_name: modelName,
      deployment: deploymentName,
      cost_prompt: Number(costPrompt),
      cost_completion: Number(costCompletion),
    });
    if (success) {
      setSnackbar({ open: true, message: 'Model added successfully.', severity: 'success' });
      handleCancelAddModel();
      refetch();
    } else {
      setSnackbar({ open: true, message: 'Failed to add model. Please try again.', severity: 'error' });
    }
  };

  const handleCancelAddModel = () => {
    setDeploymentName('');
    setModelName('');
    setCostPrompt('');
    setCostCompletion('');
    setAddModelErrors({});
    setShowAddModel(false);
  };

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">{showAddModel ? 'Add New Model' : 'Available Models'}</Typography>
        {!showAddModel && (
          <Button
            variant="contained"
            className="primary-btn"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => setShowAddModel(true)}
          >
            Add Model
          </Button>
        )}
      </Stack>

      {/* ── Inline Add Model Form ── */}
      {showAddModel && (
        <>
          <Card variant="outlined" sx={{ p: 3, mb: 3, maxWidth: 520 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <label htmlFor="deployment-name">Deployment Name</label>
              <TextField
                id="deployment-name"
                value={deploymentName}
                onChange={(e) => {
                  setDeploymentName(e.target.value);
                  setAddModelErrors((prev) => ({ ...prev, deploymentName: undefined }));
                }}
                placeholder="Enter deployment name"
                variant="outlined"
                error={!!addModelErrors.deploymentName}
                helperText={addModelErrors.deploymentName}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <label htmlFor="model-name">Model Name</label>
              <TextField
                id="model-name"
                value={modelName}
                onChange={(e) => {
                  setModelName(e.target.value);
                  setAddModelErrors((prev) => ({ ...prev, modelName: undefined }));
                }}
                placeholder="Enter model name"
                variant="outlined"
                error={!!addModelErrors.modelName}
                helperText={addModelErrors.modelName}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <label htmlFor="cost-prompt">Cost (Prompt)</label>
              <TextField
                id="cost-prompt"
                value={costPrompt}
                onChange={(e) => {
                  setCostPrompt(e.target.value);
                  setAddModelErrors((prev) => ({ ...prev, costPrompt: undefined }));
                }}
                placeholder="e.g. 0.012"
                variant="outlined"
                type="number"
                inputProps={{ min: 0, step: 'any' }}
                error={!!addModelErrors.costPrompt}
                helperText={addModelErrors.costPrompt}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <label htmlFor="cost-completion">Cost (Completion)</label>
              <TextField
                id="cost-completion"
                value={costCompletion}
                onChange={(e) => {
                  setCostCompletion(e.target.value);
                  setAddModelErrors((prev) => ({ ...prev, costCompletion: undefined }));
                }}
                placeholder="e.g. 0.02"
                variant="outlined"
                type="number"
                inputProps={{ min: 0, step: 'any' }}
                error={!!addModelErrors.costCompletion}
                helperText={addModelErrors.costCompletion}
              />
            </FormControl>

            <Stack direction="row" spacing={2} alignItems={'center'} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleCancelAddModel} disabled={addLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                className="primary-btn"
                size="large"
                onClick={handleAddModel}
                disabled={addLoading}
              >
                {addLoading ? 'Adding...' : 'Add'}
              </Button>
            </Stack>
          </Card>
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      {/* ── Models Table ── */}
      {modelsLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {modelsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {modelsError}
        </Alert>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {!modelsLoading && !modelsError && (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map((col) => (
                  <TableCell key={col.id} sx={{ fontWeight: 'bold' }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {models && models.length > 0 ? (
                models.map((model: LLMModel) => (
                  <TableRow hover key={model.id}>
                    <TableCell>{model.id}</TableCell>
                    <TableCell>{model.model_name}</TableCell>
                    <TableCell>{model.deployment}</TableCell>
                    <TableCell>{model.cost_prompt}</TableCell>
                    <TableCell>{model.cost_completion}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleOpenMenu(e, model)}>
                        <Iconify icon="eva:more-vertical-fill" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No models found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Three-dots action menu ── */}
      <Popover
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem onClick={handleOpenEdit}>
          <Iconify icon="eva:edit-fill" sx={{ mr: 2 }} />
          Edit
        </MenuItem>
        <MenuItem sx={{ color: 'error.main' }} onClick={handleDelete} disabled={deleteLoading}>
          <Iconify icon="eva:trash-2-outline" sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>

      {/* ── Edit Model Dialog ── */}
      <Dialog open={editDialogOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Model — {selectedModel?.model_name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Deployment Name"
              value={editForm.deployment}
              onChange={(e) => setEditForm((f) => ({ ...f, deployment: e.target.value }))}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="SDK Type"
              value={editForm.sdk_type}
              onChange={(e) => setEditForm((f) => ({ ...f, sdk_type: e.target.value }))}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Max Tokens"
              value={editForm.max_tokens}
              onChange={(e) => setEditForm((f) => ({ ...f, max_tokens: e.target.value }))}
              fullWidth
              variant="outlined"
              type="number"
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Cost (Prompt)"
              value={editForm.cost_prompt}
              onChange={(e) => setEditForm((f) => ({ ...f, cost_prompt: e.target.value }))}
              fullWidth
              variant="outlined"
              type="number"
              inputProps={{ min: 0, step: 'any' }}
            />
            <TextField
              label="Cost (Completion)"
              value={editForm.cost_completion}
              onChange={(e) => setEditForm((f) => ({ ...f, cost_completion: e.target.value }))}
              fullWidth
              variant="outlined"
              type="number"
              inputProps={{ min: 0, step: 'any' }}
            />
            <TextField
              label="Description"
              value={editForm.description}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              variant="outlined"
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={updateLoading}>
            Cancel
          </Button>
          <Button variant="contained" className="primary-btn" onClick={handleSaveEdit} disabled={updateLoading}>
            {updateLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
